export const name = 'rnSession';
/**
 * @ngdoc service
 * @name rhino.components.session.service:rnSession
 * @description
 * Provides the client application with security / authentication related session-based functions that enable the
 * client application to authenticate to the genesis eco-system.
 */
export default function rnSession($log, $http, rnHttp, $q, $window, rnRegistry, rnSingSession, rnAuthService, rnApplication) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  var self = this;
  const AAA_SERVICE_NAME         = 'aaa';
  const USER_VALIDATION_PATH     = 'users/validate';
  const USER_SIGNIN_PATH         = 'users/login';
  const USER_SIGNOUT_PATH        = 'users/logout';
  const USER_CONTEXTS_PATH       = 'users/context';
  const USER_SWITCH_CONTEXT_PATH = 'users/context';

  var cachedEnityInfo            = null;

  var service = {
    // New Session API (currently code is in rnSingSession) [Session Singleton]
    // this API is being duplicated here so that we don't have to refactor code twice.
    // No client code should reference rnSingSession.  We forward the API from here to rnSingSession
    // and when this old API is gone and everything uses rnAuthService rnSingSession will become rnSession.
    id: rnSingSession.id,
    user: rnSingSession.user,
    context: rnSingSession.context,
    // Uncomment the next line as part of step 5 (below)
    // isAuthenticated: rnSingSession.isAuthenticated,
    _create: rnSingSession._create,
    _destroy: rnSingSession._destroy,

    // Special case: old rnSession isAuthenticated returns a promise - the new one (in rnSingSession) will return boolean.
    // To accommodate this we will return a promise from here for now.
    // To migrate: (currently on step 1)
    //  1. Make rnSession return a wrapped boolean from rnSingSession as a promise.
    //  2. Refactor client code to use rnAuthService if a promise is need for isAuthenticated
    //  3. Change above isAuthenticated interface to return a boolean (not a promise)
    //  4. Refactor client code that can be simplified by using a boolean to use rnSession.isAuthenticated === true
    //  5. Delete isAuthenticated entry below and uncomment the one above
    //
    // Delete the next line as part of step 5 (above)
    isAuthenticated: isAuthenticated,
    isAuthenticatedNow: rnSingSession.isAuthenticated,


    // Old rnSession API - these will be forwarded to new AuthService APIs as needed.
    // Eventually all of these should be removed in favor of using rnAuthService
    // To migrate: (currently on step 1)
    //  1. Forward API to rnAuthService, add $log.warn statements that the client should use the new API
    //  2. APIs that cannot be forwarded need to have $log.warn (or $error) statements that explain new usage
    //  3. Refactor client code to use rnAuthService
    //  4. Remove old APIs from here once client code is refactored (PSI8.3 at the latest)
    //

    //need to be forwarded
    isUserValidationUrl: isUserValidationUrl,
    isUserSigninUrl: isUserSigninUrl,

    userInfo: getUserInfo,
    updateUserInfo: updateUserInfo,

    userContext: getUserContext,
    availableContexts: getAvailableContexts,
    switchContext: switchContext,
    clearCache: clearCache,

    signin: signin,
    signout: signout,



    // Probably not part of a session - these should not be here.  Need to host the reloadApp API in the app broker.
    // Forward the API from here if it makes sense.  reloadUXF needs to be removed.
    reloadApp: reloadApp,
    reloadUXF: reloadUXF,

    // Definitely not part of a session - these need to be moved to another component ASAP.
    systemVersions: getSystemVersions,
    systemVideos: getSystemVideos

  };

  return service;

  /////////////////////
  // Private functions
  ////////////////////

  /**
   * @ngdoc function
   * @name isUserValidationUrl
   * @methodOf rhino.components.session.service:rnSession
   * @description Checks if a URL is the user validation URL.
   * (Needed by the session expiration interceptor and to centralize this URL here).
   * @param {string} url to check
   * @returns {boolean} `true` if the URL is the user validation URL
   */
  function isUserValidationUrl(url) {
    // Cheating here so I don't have to turn this method into async.
    return url.indexOf(USER_VALIDATION_PATH) > -1;
  }

  /**
   * @ngdoc function
   * @name isUserSigninUrl
   * @methodOf rhino.components.session.service:rnSession
   * @description Checks if a URL is the user signin URL.
   * (Needed by the session expiration interceptor and to centralize this URL here).
   * @param {string} url to check
   * @returns {boolean} `true` if the URL is the user sign-in URL
   */
  function isUserSigninUrl(url) {
    // Cheating here so I don't have to turn this method into async.
    return url.indexOf(USER_SIGNIN_PATH) > -1;
  }

  /**
   * @ngdoc function
   * @name clearCache
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description
   * Clear the cached entity information and set the flag so that $http won't use it's cache next time the call is made.
   *
   * @return {rnSession} service.  Returns the service so this method is chainable  i.e. rnSession.clearCache().isAuthenticated()
   */
  function clearCache() {
    $log.warn(`$name}#clearCache is deprecated with no replacement`);
    // cachedEnityInfo = null;
    // cacheResponse = false;
    return service;
  }

  function isAuthenticated() {
    if (rnSingSession.isAuthenticated() !== undefined) {
      return $q.when(rnSingSession.isAuthenticated());
    } else {
      let q = $q.defer();
      rnAuthService.validate()
        .catch(() => {
          // no-op - it's ok if we fail validation here. Squelching angular 1.6 unhandled promise rejection.
        })
        .finally(() => {
          q.resolve(rnSingSession.isAuthenticated());
        });
      return q.promise;
    }
  }

  /**
   * @ngdoc function
   * @name getUserInfo
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Return cached user info. If there is no cache then call getEntityInfo to refresh from AAA
   * and return the user info portion.
   * @returns {*} cached user info
   */
  function getUserInfo() {
    if (rnSingSession.isAuthenticated() !== undefined) {
      return $q.when(rnSingSession.user);
    } else {
      let q = $q.defer();
      rnAuthService.validate()
      .finally(() => {
        q.resolve(rnSingSession.user);
      });
      return q.promise;
    }
  }

  /**
   * @ngdoc function
   * @name getUserContext
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Return cached context organization ID. If there is no cache then call
   * getEntityInfo to refresh from AAA and return the context organization ID.
   * @returns {*} the cached user context org ID
   */
  function getUserContext() {
    if (rnSingSession.isAuthenticated() !== undefined) {
      return $q.when(rnSingSession.context);
    } else {
      let q = $q.defer();
      rnAuthService.validate()
      .finally(() => {
        q.resolve(rnSingSession.context);
      });
      return q.promise;
    }
  }

  /**
   * @ngdoc function
   * @name getAvailableContexs
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Get the various contexts that are available for this authenticated session.
   * @returns {Promise<ContextContainer>} a promise of available contexts
   */
  function getAvailableContexts() {
    let deferred = $q.defer();
    this.isAuthenticated()
    .then(auth => {
      if (auth) {
        let config = {
          params: {
            limit: 10000
          }
        };

        rnHttp.get(AAA_SERVICE_NAME, USER_CONTEXTS_PATH, config)
        .then(resp => {
            deferred.resolve(resp.data);
        });
      } else {
        deferred.reject({status: 401, statusText: 'Unauthenticated'});
      }
    });
    return deferred.promise;
  }

  /**
   * @ngdoc function
   * @name switchContext
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Change the user session context to the given context
   * @param {object} context - the new context to switch into...
   * @returns {Promise} with the server response
   */
  function switchContext(context) {
    let deferred = $q.defer();

    if (!context) {
      deferred.reject({status: 400, statusText: 'Invalid Context'});
    } else {
      rnHttp.put(AAA_SERVICE_NAME, USER_SWITCH_CONTEXT_PATH, context)
      .then(resp => {
        // Use ga() global directly because we need to start a new analytics session.
        $window.ga('send', 'event', {
          sessionControl: 'start',
          eventCategory: 'account',
          eventAction: 'org context switch',
          eventLabel: rnSingSession.context.id + '|' + context.id,
        });

        deferred.resolve(resp.data);
      })
      .catch(resp => {
        deferred.reject(resp);
      });
    }
    return deferred.promise;
  }

  /**
   * @ngdoc function
   * @name signin
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Sign-in the user into the genesis eco-system
   * @param {object} signin_data to sign in with
   * @returns {Promise} containing the sign in result
   */
  function signin(signin_data) {
    $log.warn(`${name}#signin is deprecated - use rnAuthService#signin instead`);
    return rnAuthService.signin(signin_data);
  }


  /**
   * @ngdoc function
   * @name signout
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Sign-out the currently signed in user from the genesis eco-system
   * @returns {Promise} server response
   */
  function signout() {
    $log.warn(`${name}#signout is deprecated - use rnAuthService#signout instead`);
    return rnAuthService.signout();
  }


  /**
   * @ngdoc reload
   * @name clearCache
   * @methodOf rhino.components.session.service:rnSession
   *
   * @description Reload the entire page... This is a hard reload of the client side application
   * see rnApplication - this API should probably not be here - it should be on the rnApplication
   * leaving them both for now because we use this a lot on rnSession.
   *
   * @param {boolean} goHome - Force the browser to load '/' if true, else just reload the existing route
   */
  function reloadApp(goHome) {
    $log.warn(`${name}#reloadApp() is deprecated - please call rnApplication.rebootApp() instead`);
    rnApplication.rebootApp(goHome);
  }






//TODO: The rest of this file contains APIs that are not part of the user session - they need to be moved somewhere else

  // Not part of a session - these should not be here....
  const SYSTEM_VIDEO            = 'https://s3.amazonaws.com/genesis_prod_public/assets/videos/training/videolist.json?callback=JSON_CALLBACK';
  var systemVersionInfo;
  var systemVideoList;


  /**
   * Reload the entire page... This is a hard reload of the client side application
   * @param goHome - Force the browser to load '/' if true, else just reload the existing route
   */
  function reloadUXF(goHome) {
    $log.warn(`${name}#reloadUXF() is deprecated - please call rnApplication.rebootApp() instead`);
    rnApplication.rebootApp(goHome);
  }

  function getSystemInfo(){
    let SMS_PORTAL              = 'sms_portal';
    let SYSTEM_VERSIONING       = 'versions/versions.json';

    var deferredInfo = $q.defer();
    rnHttp.get(SMS_PORTAL, SYSTEM_VERSIONING)
      .then(function(resp){
        $log.debug("success in getSystemInfo");
        systemVersionInfo = resp.data;
        deferredInfo.resolve(systemVersionInfo);
      })
      .catch(function(resp){
        $log.warn("Failed to get system versioning info");
        systemVersionInfo = null;
        deferredInfo.reject(null);
      });
    return deferredInfo.promise;

  }

    /**
   * Return system versioning info.
   * @returns {*}
   */
  function getSystemVersions() {
    var deferred = $q.defer();
    $log.debug("in getSystemVersions");
    getSystemInfo()
    .then(function(data) {
      $log.debug("getSystemVersions success");
      deferred.resolve(_.sortBy(data, 'name'));
    })
    .catch(function() {
      $log.warn("getSystemVersions failed");
      deferred.resolve(null);
    });
    return deferred.promise;
  }

  /**
   * Return system video info for home page How Do I widget.
   * @returns {*}
   */
  function getSystemVideos() {
    var deferred = $q.defer();
    $log.debug("in getSystemVideos");
    getSystemVideoList().then(function(data) {
        $log.debug("getSystemVideo success");
        deferred.resolve(data);
      },
      function() {
        $log.warn("getSystemVideo failed");
        deferred.resolve(null);
      });
    return deferred.promise;
  }



  function getSystemVideoList(){
    var deferredInfo = $q.defer();
    //For CORS issue

    $http.jsonp(SYSTEM_VIDEO)
      .success(function(data){
        $log.debug("success in getSystemVideoList");
        systemVideoList = data;
        deferredInfo.resolve(systemVideoList);
      })
      .error(function(data, status){
        $log.warn("Failed to get system video info " + status);
        $log.warn("System video list: ", data);
        systemVideoList = null;
        deferredInfo.reject(null);
      })
      .finally(function(){
        $log.debug("getSystemVideoList in finally");
      });
    return deferredInfo.promise;

  }

  function updateUserInfo(userInfo) {
    rnSingSession.user = userInfo;
  }
}



// WEBPACK FOOTER //
// ../~/@printos/rhino/src/components/session/session.service.js